import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, List, Spin, Button, Row, Col, Select, DatePicker, Space, Typography, Input, Alert } from 'antd';
import { useSelector } from 'react-redux';
import moment from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Text } = Typography;

const SyncOrdersModal = ({ visible, onOk, onCancel, loading }) => {
  const sellerAccounts = useSelector(state => state.dataCommon.sellerAccounts);
  const [selectedIds, setSelectedIds] = useState([]);
  const [type, setType] = useState('ALL'); // 'ALL', 'FBA', or 'FBM'
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'days'),
    moment()
  ]);
  const [amazonOrderIds, setAmazonOrderIds] = useState('');

  useEffect(() => {
    if (visible && sellerAccounts) {
      // If amazonOrderIds is provided, only allow single seller selection
      if (amazonOrderIds.trim()) {
        setSelectedIds(sellerAccounts.length > 0 ? [sellerAccounts[0].id] : []);
      } else {
        setSelectedIds(sellerAccounts.map(acc => acc.id)); // Default select all
      }
    }
  }, [visible, sellerAccounts, amazonOrderIds]);

  const onChange = (checkedValues) => {
    // If amazonOrderIds is provided, only allow single selection
    if (amazonOrderIds.trim()) {
      setSelectedIds(checkedValues.slice(-1)); // Keep only the last selected
    } else {
      setSelectedIds(checkedValues);
    }
  };
  
  const handleTypeChange = (value) => {
    setType(value);
  };
  
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleAmazonOrderIdsChange = (e) => {
    const value = e.target.value;
    setAmazonOrderIds(value);
    
    // If amazonOrderIds is entered, restrict to single seller selection
    if (value.trim() && selectedIds.length > 1) {
      setSelectedIds(selectedIds.slice(0, 1));
    }
  };

  // Parse and validate Amazon Order IDs
  const parseAmazonOrderIds = () => {
    if (!amazonOrderIds.trim()) return [];
    
    return amazonOrderIds
      .split(/[\n,\s]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0)
      .slice(0, 50); // Limit to 50 as per Amazon API
  };

  const validOrderIds = parseAmazonOrderIds();
  const hasOrderIds = validOrderIds.length > 0;

  const handleOk = () => {
    // Format dateRange to 'YYYY-MM-DD' format when submitting
    const formattedDateRange = hasOrderIds ? null : dateRange.map(date => date.format('YYYY-MM-DD'));
    onOk(selectedIds, type, formattedDateRange, hasOrderIds ? validOrderIds : null);
  };

  return (
    <Modal
      title="Đồng bộ đơn hàng từ Amazon"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Đồng bộ"
      cancelText="Hủy"
      confirmLoading={loading}
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text strong>Amazon Order IDs (tùy chọn):</Text>
          <TextArea
            style={{ marginTop: 8 }}
            rows={4}
            placeholder="Nhập danh sách Amazon Order IDs (cách nhau bằng dấu phẩy hoặc xuống dòng). Tối đa 50 Order IDs.&#10;Ví dụ: 111-1234567-1234567, 111-1234567-1234568"
            value={amazonOrderIds}
            onChange={handleAmazonOrderIdsChange}
          />
          {hasOrderIds && (
            <Alert
              style={{ marginTop: 8 }}
              message={`Đã nhập ${validOrderIds.length} Order ID${validOrderIds.length > 1 ? 's' : ''}. Khi sử dụng Order IDs, chỉ có thể chọn 1 seller account và khoảng thời gian sẽ bị bỏ qua.`}
              type="info"
              showIcon
            />
          )}
        </div>

        <div>
          <Text strong>Loại đơn hàng:</Text>
          <Select 
            style={{ width: '100%', marginTop: 8 }}
            value={type}
            onChange={handleTypeChange}
          >
            <Option value="ALL">Tất cả đơn hàng</Option>
            <Option value="FBA">Chỉ đơn FBA</Option>
            <Option value="FBM">Chỉ đơn FBM</Option>
          </Select>
        </div>
        
        {!hasOrderIds && (
          <div>
            <Text strong>Khoảng thời gian:</Text>
            <RangePicker 
              style={{ width: '100%', marginTop: 8 }}
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
        )}
        
        <div>
          <Text strong>Seller Accounts:</Text>
          {sellerAccounts.length === 0 ? (
            <Spin />
          ) : (
            <>
              {!hasOrderIds && (
                <Row gutter={8} style={{ marginTop: 8, marginBottom: 8 }}>
                  <Col>
                    <Button size="small" onClick={() => setSelectedIds(sellerAccounts.map(acc => acc.id))}>
                      Chọn tất cả
                    </Button>
                  </Col>
                  <Col>
                    <Button size="small" onClick={() => setSelectedIds([])}>
                      Bỏ tất cả
                    </Button>
                  </Col>
                </Row>
              )}
              <Checkbox.Group
                style={{ width: '100%' }}
                value={selectedIds}
                onChange={onChange}
              >
                <List
                  dataSource={sellerAccounts}
                  renderItem={item => (
                    <List.Item>
                      <Checkbox 
                        value={item.id}
                        disabled={hasOrderIds && selectedIds.length > 0 && !selectedIds.includes(item.id)}
                      >
                        {item.name} ({item.sellerId})
                      </Checkbox>
                    </List.Item>
                  )}
                />
              </Checkbox.Group>
            </>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default SyncOrdersModal;